import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Liquid hid unselected variant stills. Re-run after remorph.
    // Selected color still is first; video stays in the well as a later slide.
    this.#enforceHideOtherVariants();
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
        if (newMediaGallery instanceof MediaGallery) {
          newMediaGallery.enforceHideOtherVariants();
        } else if (typeof newMediaGallery.enforceHideOtherVariants === 'function') {
          newMediaGallery.enforceHideOtherVariants();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Public wrapper so a remorph can re-filter immediately, before upgrade.
   */
  enforceHideOtherVariants() {
    this.#enforceHideOtherVariants();
  }

  /**
   * Keep the selected color still. Other variant-linked stills stay hidden.
   * Unlinked video stays in the well as a later slide.
   */
  #enforceHideOtherVariants() {
    if (this.dataset.hideOtherVariants !== 'true') return;

    const selectedId = this.dataset.selectedMediaId || '';

    const nodes = this.querySelectorAll('[data-media-id], [data-media-type], .product-media-container');
    const toRemove = [];

    nodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const mediaId = node.dataset.mediaId || '';
      const variantLinked = node.dataset.variantLinked === 'true';
      const isSelected = node.dataset.selectedVariantMedia === 'true' || (selectedId && mediaId === selectedId);

      let drop = false;
      if (variantLinked && !isSelected) drop = true;

      if (!drop) return;

      const item =
        node.closest('li.product-media-container, li[data-media-id], slideshow-slide, .dialog-thumbnails-list__thumbnail') ||
        (node.matches('li, slideshow-slide') ? node : null);
      if (item && !toRemove.includes(item)) toRemove.push(item);
      else if (!item && node.matches('.product-media-container') && !toRemove.includes(node)) toRemove.push(node);
    });

    toRemove.forEach((el) => el.remove());

    const grid = this.querySelector('[data-testid="media-gallery-grid"]');
    const remaining = grid
      ? grid.querySelectorAll(':scope > li')
      : this.querySelectorAll('slideshow-slide, .product-media-container--image, .product-media-container--video');
    if (remaining.length <= 1) {
      this.classList.add('media-gallery--single');
      this.querySelector('.product-media-gallery__slideshow')?.classList.add(
        'product-media-gallery__slideshow--single-media',
        'slideshow--single-media'
      );
    } else {
      const selectedId = this.dataset.selectedMediaId || '';
      const slides = this.querySelectorAll('slideshow-slide');
      let idx = 0;
      slides.forEach((slide, i) => {
        const id = slide.dataset.mediaId || slide.querySelector('[data-media-id]')?.dataset.mediaId || '';
        if (selectedId && id === selectedId) idx = i;
      });
      this.slideshow?.select(idx, undefined, { animate: false });
    }
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
